import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../../types/supabase';

export type Mascot = {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  category: string | null;
  levels: MascotLevel[];
};

export type MascotLevel = {
  id: string;
  mascot_id: string;
  level_number: number;
  min_user_level: number;
  image_url: string;
};

export type UserMascot = {
  mascot: Mascot;
  current_level: MascotLevel;
};

// Busca todos os mascotes ativos e seus níveis
export async function getAllMascots(supabase: SupabaseClient<Database>): Promise<Mascot[]> {
  // Busca mascotes ativos
  const { data: mascots, error: mascotsError } = await supabase
    .from('mascots')
    .select('*')
    .eq('is_active', true);
  if (mascotsError || !mascots) return [];

  // Busca todos os níveis
  const { data: levels, error: levelsError } = await supabase
    .from('mascot_levels')
    .select('*');
  if (levelsError || !levels) return mascots.map(m => ({ ...m, levels: [] }));

  // Agrupa níveis por mascote
  return mascots.map(mascot => ({
    ...mascot,
    levels: levels.filter(l => l.mascot_id === mascot.id).sort((a, b) => a.level_number - b.level_number)
  }));
}

// Função para registrar troca de mascote
export async function logMascotChange(supabase: SupabaseClient<Database>, userId: string, oldMascotId: string | null, newMascotId: string, userLevel: number) {
  await supabase.from('user_mascot_change_log').insert({
    user_id: userId,
    old_mascot_id: oldMascotId,
    new_mascot_id: newMascotId,
    user_level: userLevel
  });
}

// Função para buscar trocas desde o último reset
export async function getMascotChangeCount(supabase: SupabaseClient<Database>, userId: string, userLevel: number): Promise<{count: number, lastResetLevel: number}> {
  // O reset ocorre a cada múltiplo de 20 (21, 41, 61, ...)
  const lastResetLevel = Math.floor((userLevel - 1) / 20) * 20 + 1;
  const yearStart = new Date(new Date().getFullYear(), 0, 1);
  const { count, error } = await supabase
    .from('user_mascot_change_log')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('user_level', lastResetLevel)
    .gte('changed_at', yearStart.toISOString());
  return { count: count || 0, lastResetLevel };
}

// Função para verificar se pode trocar
export async function canChangeMascot(supabase: SupabaseClient<Database>, userId: string, userLevel: number): Promise<{canChange: boolean, remaining: number, lastResetLevel: number}> {
  const { count, lastResetLevel } = await getMascotChangeCount(supabase, userId, userLevel);
  return { canChange: count < 3, remaining: 3 - count, lastResetLevel };
}

// Atualizar chooseMascot para aplicar as regras
export async function chooseMascot(supabase: SupabaseClient<Database>, userId: string, mascotId: string, userLevel: number): Promise<{ success: boolean; error?: string }> {
  // Verifica se já escolheu
  const { data: existing, error: existingError } = await supabase
    .from('user_mascots')
    .select('id, mascot_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (existingError) return { success: false, error: 'Erro ao verificar mascote do usuário.' };
  if (existing && existing.mascot_id === mascotId) return { success: false, error: 'Você já está com este mascote.' };

  // Verifica limite de troca
  const { canChange, remaining } = await canChangeMascot(supabase, userId, userLevel);
  if (!canChange) return { success: false, error: 'Limite de trocas de mascote atingido. Aguarde subir mais 20 níveis ou o próximo ano para trocar novamente.' };

  // Atualiza mascote
  if (existing) {
    const { error: updateError } = await supabase
      .from('user_mascots')
      .update({ mascot_id: mascotId, chosen_at: new Date().toISOString() })
      .eq('id', existing.id);
    if (updateError) return { success: false, error: 'Erro ao trocar mascote.' };
    await logMascotChange(supabase, userId, existing.mascot_id, mascotId, userLevel);
  } else {
    const { error: insertError } = await supabase
      .from('user_mascots')
      .insert({ user_id: userId, mascot_id: mascotId });
    if (insertError) return { success: false, error: 'Erro ao escolher mascote.' };
    await logMascotChange(supabase, userId, null, mascotId, userLevel);
  }
  return { success: true };
}

// Busca o mascote do usuário e a imagem correspondente ao nível atual
export async function getUserMascot(supabase: SupabaseClient<Database>, userId: string, userLevel: number): Promise<UserMascot | null> {
  // Busca mascote escolhido
  const { data: userMascotRow, error: userMascotError } = await supabase
    .from('user_mascots')
    .select('mascot_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (userMascotError || !userMascotRow) return null;

  // Busca mascote
  const { data: mascot, error: mascotError } = await supabase
    .from('mascots')
    .select('*')
    .eq('id', userMascotRow.mascot_id)
    .maybeSingle();
  if (mascotError || !mascot) return null;

  // Busca níveis do mascote
  const { data: levels, error: levelsError } = await supabase
    .from('mascot_levels')
    .select('*')
    .eq('mascot_id', mascot.id);
  if (levelsError || !levels) return null;

  // Determina o nível atual do mascote com base no nível do usuário
  const sortedLevels = levels.sort((a, b) => a.level_number - b.level_number);
  let currentLevel = sortedLevels[0];
  for (const lvl of sortedLevels) {
    if (userLevel >= lvl.min_user_level) {
      currentLevel = lvl;
    } else {
      break;
    }
  }

  return {
    mascot: { ...mascot, levels: sortedLevels },
    current_level: currentLevel
  };
}

// --- Funções administrativas ---

// Lista todos os mascotes (ativos e inativos) e seus níveis
export async function getAllMascotsWithLevels(supabase: SupabaseClient<Database>): Promise<Mascot[]> {
  const { data: mascots, error: mascotsError } = await supabase
    .from('mascots')
    .select('*');
  if (mascotsError || !mascots) return [];

  const { data: levels, error: levelsError } = await supabase
    .from('mascot_levels')
    .select('*');
  if (levelsError || !levels) return mascots.map(m => ({ ...m, levels: [] }));

  return mascots.map(mascot => ({
    ...mascot,
    levels: levels.filter(l => l.mascot_id === mascot.id).sort((a, b) => a.level_number - b.level_number)
  }));
}

// Cria um novo mascote
export async function createMascot(supabase: SupabaseClient<Database>, mascot: { name: string; description?: string | null; is_active?: boolean; category?: string | null }): Promise<{ success: boolean; mascot?: Mascot; error?: string }> {
  const { data, error } = await supabase
    .from('mascots')
    .insert({
      name: mascot.name,
      description: mascot.description || null,
      is_active: mascot.is_active !== undefined ? mascot.is_active : true,
      category: mascot.category || null
    })
    .select()
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, mascot: { ...data, levels: [] } };
}

// Atualiza um mascote existente
export async function updateMascot(supabase: SupabaseClient<Database>, mascotId: string, updates: { name?: string; description?: string | null; is_active?: boolean; category?: string | null }): Promise<{ success: boolean; mascot?: Mascot; error?: string }> {
  const { data, error } = await supabase
    .from('mascots')
    .update(updates)
    .eq('id', mascotId)
    .select()
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, mascot: { ...data, levels: [] } };
}

// Deleta um mascote (e todos os níveis associados)
export async function deleteMascot(supabase: SupabaseClient<Database>, mascotId: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('mascots')
    .delete()
    .eq('id', mascotId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

// Cria um novo nível de mascote
export async function createMascotLevel(supabase: SupabaseClient<Database>, level: { mascot_id: string; level_number: number; min_user_level: number; image_url: string }): Promise<{ success: boolean; level?: MascotLevel; error?: string }> {
  const { data, error } = await supabase
    .from('mascot_levels')
    .insert(level)
    .select()
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, level: data };
}

// Atualiza um nível de mascote
export async function updateMascotLevel(supabase: SupabaseClient<Database>, levelId: string, updates: { level_number?: number; min_user_level?: number; image_url?: string }): Promise<{ success: boolean; level?: MascotLevel; error?: string }> {
  const { data, error } = await supabase
    .from('mascot_levels')
    .update(updates)
    .eq('id', levelId)
    .select()
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, level: data };
}

// Deleta um nível de mascote
export async function deleteMascotLevel(supabase: SupabaseClient<Database>, levelId: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('mascot_levels')
    .delete()
    .eq('id', levelId);
  if (error) return { success: false, error: error.message };
  return { success: true };
} 